import type { ItemRow } from '~~/shared/items/itemRow'

/** An org-listed row: an `ItemRow` plus the membership arrays `listOrgDocuments` ships with it. */
type OrgRow = ItemRow & {
  case_ids?: string[] | null
  client_ids?: string[] | null
  conversation_ids?: string[] | null
  sheet_ids?: string[] | null
}

/**
 * Group row ids by a membership array: each row is listed under every id it names, in row order —
 * the loaded prefix of that home's membership, whose tail the server's uncapped total accounts for.
 * The ONE copy of what `fetchGroups` wrote out three times (sheets, threads, clients).
 */
export function groupByMembership<R extends { id: string }>(
  rows: R[],
  memberships: (row: R) => readonly string[] | null | undefined,
): Map<string, string[]> {
  const out = new Map<string, string[]>()
  for (const r of rows) {
    for (const key of memberships(r) ?? []) {
      const held = out.get(key)
      if (held) held.push(r.id)
      else out.set(key, [r.id])
    }
  }
  return out
}

export interface OrgDocumentBuckets {
  /** The rows as they may be cached: the membership arrays stripped (see below). */
  rows: ItemRow[]
  /** Every org-listed id, in the server's order (the active sort, AlexAmin/marcus#169). */
  groupedIds: string[]
  ungroupedIds: string[]
  conversationLinkedIds: string[]
  sheetLinkedIds: string[]
  sheetDocumentIds: Map<string, string[]>
  conversationDocumentIds: Map<string, string[]>
  unattachedIds: string[]
  clientDirectIds: Map<string, string[]>
}

const count = (ids: readonly string[] | null | undefined) => ids?.length ?? 0

/**
 * Split the org-wide listing into the buckets the grouped views read (MAR-93, #83, #150).
 *
 * Every bucket is derived from the row's own membership arrays — no second endpoint:
 * - **no case** (`ungroupedIds`) — case mode's trailing bucket, whatever else the row is attached to;
 * - **linked to a thread / a sheet** — the link ALONE, mirroring the server's `conversationLinked` /
 *   `sheetRanked` CTEs: a sheet and a chat thread are homes, so a document keeps them when it is
 *   also filed into a matter (AlexAmin/marcus#150). The old predicates demanded no case and no
 *   client and matched nothing;
 * - **attached to nothing at all** — the bottom rung, mirroring the server's `unfiled` CTE. Unlike
 *   the two above these ids are NOT excluded from `ungroupedIds`: they have no case, so case mode's
 *   "Sin asunto" is still the honest place for them.
 *
 * The membership arrays are consumed HERE and never cached. `upsert` shallow-merges and never drops
 * fields, so a cached `case_ids` would survive every later write and go stale the first time the
 * user links or unlinks — a second copy of membership in the one cache that exists to prevent them.
 * Stripping them at the boundary makes that unrepresentable rather than merely unused.
 */
export function bucketOrgDocuments(documents: OrgRow[]): OrgDocumentBuckets {
  const noCase = (r: OrgRow) => count(r.case_ids) === 0
  const noClient = (r: OrgRow) => count(r.client_ids) === 0
  const inConversation = (r: OrgRow) => count(r.conversation_ids) > 0
  const inSheet = (r: OrgRow) => count(r.sheet_ids) > 0
  const ids = (keep: (r: OrgRow) => boolean) => documents.filter(keep).map(r => r.id)

  return {
    rows: documents.map(({ case_ids: _c, client_ids: _cl, conversation_ids: _cv, sheet_ids: _s, ...row }) => row),
    groupedIds: documents.map(r => r.id),
    ungroupedIds: ids(noCase),
    conversationLinkedIds: ids(inConversation),
    sheetLinkedIds: ids(inSheet),
    sheetDocumentIds: groupByMembership(documents, r => r.sheet_ids),
    conversationDocumentIds: groupByMembership(documents, r => r.conversation_ids),
    unattachedIds: ids(r => noCase(r) && noClient(r) && !inConversation(r) && !inSheet(r)),
    clientDirectIds: groupByMembership(documents, r => r.client_ids),
  }
}
